'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="The 60-Second Bathroom Microplastic Audit: 12 Products to Scan First (2026)"
      author="Nora Vale"
      date="June 21, 2026"
      readTime="10 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="60 second bathroom microplastic audit 2026. 12 products to scan first with MicroPlastics app"
    >
      <BlogLastUpdated iso="2026-06-21" formatted="June 21, 2026" />

      <BlogTLDR>
        The average American bathroom contains <strong>30+ personal-care
        products</strong>, and <strong>roughly 70% of them have at least
        one liquid-plastic ingredient</strong> (polyethylene, PEG,
        carbomer, acrylates copolymer, dimethicone) in the formulation.
        You don&apos;t need to research each one individually, open the
        MicroPlastics app, scan the barcode on the 12 products below,
        and you&apos;ll have a complete bathroom risk audit in about a
        minute per product. The 12: <strong>(1) shampoo, (2)
        conditioner, (3) body wash, (4) hand soap, (5) sunscreen, (6)
        body lotion, (7) deodorant, (8) toothpaste, (9) mouthwash, (10)
        face moisturiser, (11) hand sanitiser, (12) lip balm</strong>.
        Start with sunscreen and deodorant (highest skin-contact load),
        finish with the 3 you use most often.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The average US bathroom has 30+ personal-care products; about 70% contain at least one hidden plastic ingredient.</>,
          <>The 12 highest-priority products for audit: shampoo, conditioner, body wash, hand soap, sunscreen, lotion, deodorant, toothpaste, mouthwash, face moisturiser, hand sanitiser, lip balm.</>,
          <>The highest-yield swap is whichever product you use most often, most adults apply the same shampoo / body wash / deodorant 300+ times a year.</>,
          <>5 ingredients to flag on every label: polyethylene, PEG-* (any number), carbomer, acrylates copolymer, dimethicone (or any -methicone/siloxane).</>,
          <>The MicroPlastics app reads the barcode + the ingredient list and returns a 0–100 risk score per product, with the cleaner same-budget swap.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why a 60-second audit beats trying to research everything
      </h2>
      <p>
        The microplastic ingredient list across modern personal-care
        products is genuinely overwhelming. The average shampoo
        ingredient list has 25–40 items; mainstream toothpastes have
        15–25; sunscreens 20–30. Researching each one individually,
        cross-referencing with the EWG database or PubChem, and tracking
        which products contain which ingredients is a project, not a
        decision.
      </p>
      <p>
        The 60-second audit reframes the problem: <strong>you scan once
        per product, get a 0–100 score, and act on the worst few</strong>.
        Most users discover that 3–5 products account for 70-80% of
        their daily plastic-ingredient exposure. Replacing those 3–5
        cuts the load disproportionately. The remaining products are
        either already-clean or low-frequency-use.
      </p>

      <BlogAppCTA
        headline="Open the app now and walk to your bathroom"
        sub="The MicroPlastics app is free with 3 scans to start. Walk to your bathroom with your phone, open the app, and start scanning. You'll have your audit done before your coffee finishes brewing."
        buttonLabel="Open the app"
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 12 products in priority order
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">1. Sunscreen</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: dimethicone, acrylates
        copolymer, polyethylene.</strong> Highest priority because
        you apply 1+ ounce per use to your largest organ (skin), often
        in summer when daily reapplication compounds exposure. See our{' '}
        <Link href="/blog/best-sunscreens-without-microplastics-brands-ranked" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best sunscreens without microplastics
        </Link>{' '}
        for cleaner picks. <strong>Scan first.</strong>
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">2. Deodorant</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: PEG, dimethicone,
        propylene glycol, acrylates copolymer.</strong> Daily use on
        thin axillary skin near lymph nodes makes this a high-exposure
        category. Almost every mainstream antiperspirant has 3-4
        plastic-flag ingredients. See our{' '}
        <Link href="/blog/microplastics-in-deodorant-brands-ranked" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          deodorant brand ranking
        </Link>
        . <strong>Scan second.</strong>
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">3. Shampoo</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: polyethylene
        (exfoliating shampoos), dimethicone (smoothing shampoos), PEG,
        acrylates copolymer.</strong> You use a quarter-sized amount
        2-4× per week, meaningful accumulated load. Plus the
        production-scale plastic ingredients wash directly into
        waterways. See{' '}
        <Link href="/blog/microplastics-in-shampoo-and-conditioner" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in shampoo &amp; conditioner
        </Link>
        .
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">4. Conditioner</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: dimethicone,
        amodimethicone, behentrimonium chloride, cyclopentasiloxane.
        </strong> The smoothing/detangling function of modern
        conditioners is achieved almost entirely with silicones, a
        cluster of related liquid plastics. &ldquo;Silicone-free&rdquo;
        conditioners (Verb, Briogeo, Innersense) are the cleaner picks.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">5. Body wash</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: sodium polyacrylate
        (thickener), PEG-7 olivate, acrylates crosspolymer, polyquaternium-7.
        </strong> Body washes apply more product per use than shampoo, and the surfactants strip the skin&apos;s natural lipid barrier
        before the plastic ingredients have a chance to interact with
        skin. Bar soap is the cleanest alternative; Dr. Bronner&apos;s
        Castile is the simplest clean option.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">6. Body lotion</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: dimethicone, carbomer,
        PEG-40 stearate, cyclopentasiloxane, polyacrylate-13.</strong>{' '}
        Lotion stays on skin and is absorbed over hours. Daily-use
        body lotions in winter or year-round-dry-skin contexts deliver
        meaningful cumulative load.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">7. Hand soap</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: PEG-150 distearate,
        polyquaternium-7, polyacrylate, acrylates copolymer.</strong>{' '}
        Frequency of use is the issue, most adults wash hands 6-15×
        per day. Bar soap (Dr. Bronner&apos;s, Kirk&apos;s) is the
        cleanest format. Refillable foam dispensers with simple liquid
        soap are second-best.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">8. Toothpaste</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: polyethylene
        (exfoliating), PEG-12, carbomer, sodium polyphosphate.</strong>{' '}
        You spit out most of it but you also swallow some. Direct
        mucous-membrane contact for 2 minutes 2× per day is
        non-trivial. See{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best microplastic-free toothpaste
        </Link>
        .
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">9. Mouthwash</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: poloxamer 407,
        polysorbate 80, PEG-40.</strong> Direct mouth contact for
        30–60 seconds, then partial swallow. Major brands (Listerine,
        Scope) all contain at least one polymer; alcohol-free natural
        versions (Tom&apos;s of Maine, Tend mouthwash) are cleaner.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">10. Face moisturiser</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: dimethicone, carbomer,
        acrylates/C10-30 alkyl acrylate crosspolymer, PEG variants.
        </strong> Direct face application with daily reapplication.
        High-end face moisturisers are often worse than mid-tier
        because the &ldquo;luxurious feel&rdquo; is silicone-based.
        Simple options: Cetaphil Daily Hydrating Lotion (basic),
        Pipette Baby Lotion (clean), Weleda Skin Food.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">11. Hand sanitiser</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: carbomer, PEG-12
        dimethicone, propylene glycol, polyacrylate.</strong> The
        gel texture of standard hand sanitiser is achieved with
        carbomer. Liquid alcohol-only formulations (PurellAdvanced
        Liquid, Touchland Power Mist) often have shorter ingredient
        lists than the gels.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">12. Lip balm</h3>
      <p>
        <strong>Most-likely-plastic-ingredient: polybutene,
        polyethylene, microcrystalline wax (petroleum derivative).
        </strong> You ingest small amounts of lip balm with normal use.
        Beeswax-based balms (Burt&apos;s Bees Original, Badger Cocoa
        Butter Lip Balm) and pure plant-oil balms (Hurraw!) are the
        cleanest picks. See{' '}
        <Link href="/blog/microplastics-in-lipstick-and-lip-balm" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in lipstick &amp; lip balm
        </Link>
        .
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 5 ingredients to flag on every label
      </h2>

      <BlogComparisonTable
        caption="The 5 most common bathroom microplastic ingredients, what they do, what to swap to"
        columns={[
          { key: 'ingredient', label: 'Ingredient' },
          { key: 'function', label: 'What it does' },
          { key: 'where', label: 'Where it&apos;s most common' },
          { key: 'swap', label: 'Cleaner alternative' },
        ]}
        rows={[
          { ingredient: 'Polyethylene', function: 'Exfoliating microspheres, thickener', where: 'Toothpaste, face scrubs, body scrubs (EU banned in rinse-off 2023)', swap: 'Sugar scrubs, jojoba beads, salt scrubs' },
          { ingredient: 'PEG-* (PEG-8, PEG-40, etc.)', function: 'Emulsifier, humectant', where: 'Lotion, body wash, hand soap, deodorant', swap: 'Plant-oil emulsions (cetearyl alcohol + vegetable glycerin)' },
          { ingredient: 'Carbomer', function: 'Polymer thickener for gels', where: 'Face creams, hand sanitiser, sunscreen', swap: 'Xanthan gum, plant gels (aloe vera-based)' },
          { ingredient: 'Acrylates copolymer / crosspolymer', function: 'Film-former, water-resistance', where: 'Sunscreen, makeup, antiperspirant, hair styling', swap: 'Plant waxes (carnauba, candelilla), beeswax' },
          { ingredient: 'Dimethicone (and -methicone / siloxane family)', function: 'Smooth-skin film, silky feel', where: 'Conditioner, lotion, sunscreen, deodorant, foundation', swap: 'Squalane, jojoba oil, argan oil' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The actual 60-second audit workflow
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>Open the MicroPlastics app on your phone, walk to the bathroom.</li>
        <li>Pick up product #1 (sunscreen).</li>
        <li>Tap scan, point camera at barcode. Wait ~2 seconds.</li>
        <li>Read the 0–100 score. If &gt;60, tap the &ldquo;cleaner alternative&rdquo; suggestion to see the same-format, similar-budget swap.</li>
        <li>Move to product #2 (deodorant). Repeat.</li>
        <li>Continue through the 12 products. Total time: ~60 seconds per product = 12 minutes for the full audit.</li>
        <li>At the end you have a ranked list of every product in your bathroom by microplastic risk score.</li>
        <li>Make a list of the 3-5 worst scoring products. These are your replacement priorities for the next time you&apos;d normally repurchase them.</li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the data usually shows
      </h2>
      <p>
        In our user audits, the typical bathroom breakdown looks like:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>3–5 products score above 70</strong> (high-risk, replace next time).</li>
        <li><strong>4–6 products score 40-70</strong> (medium-risk, swap on next repurchase when convenient).</li>
        <li><strong>2–4 products score below 40</strong> (clean, no action needed).</li>
      </ul>
      <p>
        The 3–5 high-risk items are almost always: a chemical
        sunscreen, an antiperspirant, a smoothing conditioner, a thick
        face moisturiser, and either a hand soap or body wash. Swapping
        those 3–5 produces measurable urinary phthalate and BPA
        reductions within 2-3 weeks per the &ldquo;fresh food and
        clean product&rdquo; intervention trials.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What to do with the results
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Don&apos;t throw out perfectly good products.</strong> Use what you have until it runs out, then replace with the cleaner alternative. Throwing out unopened products is bad for waste reduction and unnecessarily expensive.</li>
        <li><strong>Replace in order of use frequency.</strong> The product you use daily matters more than the one you use weekly. Replace sunscreen and deodorant first, mouthwash and hand sanitiser later.</li>
        <li><strong>Buy the cleaner alternative once.</strong> Once you&apos;ve identified the swap (Thinkbaby for sunscreen, Each &amp; Every for deodorant, Briogeo for conditioner, etc.), you don&apos;t need to re-research each time. Subscribe or repurchase the same SKU.</li>
        <li><strong>Re-audit annually.</strong> Brand reformulations happen. The Native deodorant you bought last year may be a different formulation today. Quick rescan once a year catches drift.</li>
      </ol>

      <BlogAppCTA
        headline="Take 12 minutes, audit your entire bathroom right now"
        sub="The MicroPlastics app is free, no signup, 3 scans to start. Scan your worst suspects first, identify what to replace as you repurchase. The cleanest bathroom you can build for free."
        buttonLabel="Start your bathroom audit"
      />

      <p>
        See also{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in cosmetics (full guide)
        </Link>
        ,{' '}
        <Link href="/blog/worst-microplastic-ingredients" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          worst microplastic ingredients to avoid
        </Link>
        ,{' '}
        <Link href="/blog/best-sunscreens-without-microplastics-brands-ranked" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best sunscreens without microplastics
        </Link>
        ,{' '}
        <Link href="/blog/microplastics-in-deodorant-brands-ranked" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          deodorant brand ranking
        </Link>
        ,{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best microplastic-free toothpaste
        </Link>
        , and{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          men&apos;s grooming products
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Every barcode in your bathroom, shampoo, conditioner, body wash, sunscreen, lotion, deodorant, toothpaste, mouthwash, face moisturiser, hand sanitiser, lip balm.</>,
          <>Ingredient list parser flags polyethylene, PEG-*, carbomer, acrylates copolymer / crosspolymer, dimethicone / -methicone / siloxane automatically.</>,
          <>Per-product 0–100 risk score with the cleaner same-format swap.</>,
          <>Saved audit history so you can re-scan annually and catch brand reformulations.</>,
          <>Your &ldquo;worst 5&rdquo; replacement priority list ready for your next grocery run.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'How long does a bathroom microplastic audit actually take?',
            answer:
              'About 12 minutes for the 12 priority products listed in this article, roughly 1 minute per product including reading the score and noting the cleaner alternative. A full bathroom (30+ products) takes 30-45 minutes the first time. After that you only need to rescan when you buy a new product.',
          },
          {
            question: 'Which bathroom product has the most microplastics?',
            answer:
              'Sunscreen and antiperspirant typically score highest because both combine multiple plastic-film-forming ingredients (dimethicone + acrylates copolymer + polyethylene + carbomer). Smoothing conditioners and thick face moisturisers come next. Bar soaps, simple toothpastes without polyethylene, and basic Cetaphil-style lotions usually score lowest.',
          },
          {
            question: 'What ingredients should I look for on cosmetic labels?',
            answer:
              'Five to flag on every label: polyethylene (and any &ldquo;poly&rdquo; prefix), PEG-* (any number), carbomer, acrylates copolymer or crosspolymer, dimethicone (and the broader -methicone / siloxane family). If you see 2+ of these on a product, it&apos;s a candidate to replace at next repurchase.',
          },
          {
            question: 'Are exfoliating microbeads still in toothpaste and face scrubs?',
            answer:
              'In the EU, intentionally added microplastics including polyethylene microbeads were restricted from October 2023 under REACH Annex XVII. In the US, polyethylene microbeads were banned in rinse-off cosmetics in 2017 (Microbead-Free Waters Act) but liquid polymers (PEG, carbomer, acrylates copolymer) are still allowed in leave-on products. Always check the ingredient list.',
          },
          {
            question: 'Should I throw out my current products if they score high?',
            answer:
              'No. Use what you have until it runs out, then replace with a cleaner alternative. Throwing out unopened products is wasteful and unnecessarily expensive. Microplastic exposure is cumulative over years, making the next purchase cleaner is what bends the long-term curve.',
          },
          {
            question: 'What if my product isn&apos;t in the app database?',
            answer:
              'The MicroPlastics app falls back to the ingredient-list parser when a specific SKU isn&apos;t in the polymer database. You photograph the ingredient list and the parser flags the 5 plastic-ingredient families automatically. This works for niche brands, new product launches, and international SKUs that haven&apos;t been catalogued yet.',
          },
          {
            question: 'How often should I re-audit my bathroom?',
            answer:
              'Annually for the products you keep, and any time you try a new brand. Brand reformulations happen regularly. Native deodorant, for example, has been reformulated multiple times since 2020 acquisition by P&amp;G. An annual rescan catches reformulation drift before it accumulates.',
          },
          {
            question: 'Does the MicroPlastics app cost money?',
            answer:
              'Free tier: 3 scans, enough to spot-check your worst suspects. Pro removes the scan limit (with a 3-day free trial), so a full bathroom audit is a one-session job.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'European Chemicals Agency',
            year: 2023,
            title: 'Restriction of intentionally added microplastics: REACH Annex XVII',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
          {
            authors: 'US Food and Drug Administration',
            year: 2024,
            title: 'Microbead-Free Waters Act: implementation and ongoing cosmetic policy',
            publisher: 'FDA',
            url: 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations/microbead-free-waters-act-faqs',
          },
          {
            authors: 'Environmental Working Group',
            year: 2025,
            title: 'EWG Skin Deep: cosmetic ingredient safety database',
            publisher: 'EWG',
            url: 'https://www.ewg.org/skindeep/',
          },
          {
            authors: 'Cosmetic Ingredient Review Expert Panel',
            year: 2024,
            title: 'Safety assessment of dimethicone and related silicone polymers',
            publisher: 'CIR',
            url: 'https://www.cir-safety.org/',
          },
          {
            authors: 'Harley KG, Kogut K, Madrigal DS, et al.',
            year: 2016,
            title: 'Reducing phthalate, paraben, and phenol exposure from personal care products in adolescent girls: intervention trial',
            publisher: 'Environmental Health Perspectives',
            url: 'https://ehp.niehs.nih.gov/doi/10.1289/ehp.1510514',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
